import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiIcon,
  MiInputTextbox,
  MiList,
  MiMenu,
  MiSectionHeader,
} from "@miplatform/components";
import { PAGINATION } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { IBidResponse } from "@miplatform/types";
import { createUUID, formatDate } from "@miplatform/utils";
import { mdiMagnify, mdiPencilOutline, mdiTrashCanOutline } from "@mdi/js";
import { useBids } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { useCreateBidContext } from "../../../CreateBidContext";
import { DeleteTemplateModal } from "./DeleteTemplateModal/DeleteTemplateModal";
import { RenameTemplateModal } from "./RenameTemplateModal/RenameTemplateModal";

export function AreaTemplates({ open }: AreaTemplatesProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [search, setSearch] = useState("");
  const [openRenameModal, setOpenRenameModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IBidResponse | null>(
    null
  );

  const debouncedSearch = useDebounce(search);
  const { classes, theme } = useStyles();
  const { form } = useCreateBidContext();
  const { setValue, reset } = form;
  const {
    isLoading,
    refresh,
    areaTemplates,
    setAreaTemplates,
    getAreaTemplates,
    createAreaTemplate,
    deleteBid,
  } = useBids();

  useEffect(() => {
    if (open) {
      setSelectedTemplateId("");
      reset();
      setSearch("");
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const fieldset = [
    {
      label: "Name",
      field: "name",
      renderCell: (data: IBidResponse) => {
        return (
          <Typography variant="HD3" color="primary.main" pl={2}>
            {data.name}
          </Typography>
        );
      },
    },
    {
      label: "Created Date",
      field: "createdOn",
      renderValue: (data: IBidResponse) =>
        formatDate(data.createdOn, "ddd, MMMM D, YYYY"),
    },
  ];

  const handleCardClick = (item: IBidResponse) => {
    if (item.id === selectedTemplateId) {
      setValue("planAreas", [], { shouldDirty: true });
      setSelectedTemplateId("");
    } else {
      const modifiedPlanAreas = item.planAreas.map((a) => ({
        ...a,
        id: createUUID(),
        requiredCategories: [],
      }));
      setValue("planAreas", modifiedPlanAreas);
      setSelectedTemplateId(item.id);
    }
  };

  const renderItemActions = (item: IBidResponse) => {
    const actions = [
      {
        label: "Rename",
        icon: mdiPencilOutline,
        action: () => {
          setSelectedTemplate(item);
          setOpenRenameModal(true);
        },
      },
      {
        label: "Delete",
        icon: mdiTrashCanOutline,
        color: theme.palette.error.main,
        action: () => {
          setSelectedTemplate(item);
          setOpenDeleteModal(true);
        },
      },
    ];

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    );
  };

  const modifiedAreaTemplates = areaTemplates.map((a) => ({
    ...a,
    containerStyles: a.id === selectedTemplateId && {
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.primary.accent}`,
    },
  }));

  return (
    <>
      <MiSectionHeader
        variant="text"
        title="Select Area Template"
        subtitle="Select from the list of area templates"
      />
      <MiInputTextbox
        className={classes.searchBox}
        placeholder="Search for Area Templates"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        textfieldProps={{
          startAdornment: (
            <MiIcon path={mdiMagnify} color={theme.palette.grey.medium} />
          ),
        }}
      />
      <MiList
        viewType="card"
        loading={isLoading}
        data={modifiedAreaTemplates}
        setData={setAreaTemplates}
        dataProvider={getAreaTemplates}
        refresh={refresh}
        paginationMode={PAGINATION.SERVER}
        search={debouncedSearch}
        cardProps={{
          sections: [{ fieldset }],
          onClick: handleCardClick,
          renderItemActions,
        }}
        emptyListProps={{
          text: "No Area Templates to Display",
          helperText:
            "Tip: Start from scratch, add your area, then click Save Area Template.",
          image: MiBuildHouseAlt,
          iconBgColor: theme.palette.primary.light,
          containerStyle: {
            backgroundColor: theme.palette.primary.light,
            margin: theme.spacing(1),
            borderRadius: 4,
          },
        }}
      />
      <RenameTemplateModal
        open={openRenameModal}
        closeModal={() => setOpenRenameModal(false)}
        selectedTemplate={selectedTemplate}
        updateAreaTemplate={createAreaTemplate}
      />
      <DeleteTemplateModal
        open={openDeleteModal}
        closeModal={() => {
          setOpenDeleteModal(false);
          reset();
          setSelectedTemplateId("");
        }}
        selectedTemplate={selectedTemplate}
        deleteBid={deleteBid}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  searchBox: {
    padding: theme.spacing(1, 2, 2, 2),
  },
}));

interface AreaTemplatesProps {
  open: boolean;
}
